import { CITATION_SOURCES, MONS } from '../mock/data.ts';
import type { SourceId, SpeciesId, Verdict, VerdictEvidenceRow } from '../types.ts';
import { CitationBadge } from './CitationBadge.tsx';
import { Sprite } from './Sprite.tsx';

// VerdictCard renders the structured Verdict shape the agent emits as the
// final block of a matchup-study. Renders a headline stat, a 2-sprite lead
// recommendation, and an evidence list where every row carries a citation.
//
// HARD VALIDATION (treat the LLM output as untrusted):
//   - the entire verdict is dropped if shape is malformed, headline_stat
//     lacks a registered source, or precedents_count isn't a real integer
//   - evidence rows lacking a registered source are dropped silently
//   - the `verified: true` self-attestation bypass was REMOVED — tool-derived
//     claims must cite source='damage-calc' (added to CITATION_SOURCES) so
//     every claim on screen has a citation pill, not the LLM's own word
//   - if some rows were dropped, the user sees a "N rows hidden" footer so
//     filtering is never silent
//   - if ALL rows fail, we render a "verdict suppressed" stub rather than
//     null, so the user knows the LLM's claim was rejected
export function VerdictCard({ data }: { data: Verdict | undefined }) {
  if (!data || !isValidVerdict(data)) return null;
  const evidence = data.evidence.filter(isValidEvidenceRow);
  const suppressedCount = data.evidence.length - evidence.length;

  return (
    <div
      className="vb-card fade-in"
      style={{
        borderLeftWidth: 2,
        borderLeftColor: 'var(--accent-1)',
      }}
    >
      <div className="vb-card-header">
        <span
          className="caps"
          style={{ color: 'var(--accent-2)', fontSize: 10, letterSpacing: '0.08em' }}
        >
          verdict
        </span>
        <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>
          {data.precedents_count} precedents
        </span>
      </div>

      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <HeadlineStat stat={data.headline_stat} />
        <LeadRecommendation pair={data.lead_pair} />
        {evidence.length > 0 ? (
          <EvidenceList rows={evidence} />
        ) : (
          <SuppressedAllStub originalCount={data.evidence.length} />
        )}
        {suppressedCount > 0 && evidence.length > 0 ? (
          <SuppressedFooter count={suppressedCount} />
        ) : null}
      </div>
    </div>
  );
}

function HeadlineStat({ stat }: { stat: Verdict['headline_stat'] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
      <span
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 18,
          color: 'var(--fg-1)',
          lineHeight: 1.3,
        }}
      >
        {stat.text.slice(0, 200)}
      </span>
      <CitationBadge source={stat.source} suffix={stat.suffix} />
    </div>
  );
}

function LeadRecommendation({ pair }: { pair: [SpeciesId, SpeciesId] }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        background: 'var(--accent-bg)',
        border: '1px solid var(--accent-line)',
        borderRadius: 4,
        flexWrap: 'wrap',
      }}
    >
      <span
        className="caps mono"
        style={{ color: 'var(--accent-2)', fontSize: 9.5, letterSpacing: '0.1em' }}
      >
        lead
      </span>
      <Sprite species={pair[0]} size={36} />
      <span style={{ color: 'var(--fg-1)', fontWeight: 500, fontSize: 13 }}>
        {MONS[pair[0]]?.name ?? pair[0]} <span style={{ color: 'var(--fg-3)' }}>+</span>{' '}
        {MONS[pair[1]]?.name ?? pair[1]}
      </span>
      <Sprite species={pair[1]} size={36} />
    </div>
  );
}

function EvidenceList({ rows }: { rows: VerdictEvidenceRow[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((row, i) => (
        <div
          key={`${row.stat}::${row.source}::${row.suffix}::${i}`}
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'baseline',
            padding: '4px 0',
            borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
            fontStyle: row.quote ? 'italic' : 'normal',
          }}
        >
          <span style={{ color: 'var(--fg-1)', fontSize: 13, flex: 1, minWidth: 0 }}>
            {row.quote ? `"${row.stat}"` : row.stat}
          </span>
          {row.source ? <CitationBadge source={row.source} suffix={row.suffix} /> : null}
        </div>
      ))}
    </div>
  );
}

function SuppressedFooter({ count }: { count: number }) {
  return (
    <div
      className="mono"
      style={{
        fontSize: 10,
        color: 'var(--fg-3)',
        paddingTop: 4,
        borderTop: '1px dashed var(--line-1)',
      }}
      title="The agent emitted evidence rows that lacked a registered source. They were filtered out to prevent un-cited claims from reaching you."
    >
      {count} evidence {count === 1 ? 'row' : 'rows'} hidden — missing citation
    </div>
  );
}

function SuppressedAllStub({ originalCount }: { originalCount: number }) {
  return (
    <div
      className="mono"
      style={{
        fontSize: 11,
        color: 'var(--fg-3)',
        padding: '8px 10px',
        background: 'var(--bg-2)',
        border: '1px dashed var(--line-1)',
        borderRadius: 4,
      }}
    >
      verdict suppressed — all {originalCount} evidence {originalCount === 1 ? 'row' : 'rows'}{' '}
      lacked a citation
    </div>
  );
}

export function isValidVerdict(v: unknown): v is Verdict {
  if (typeof v !== 'object' || v === null) return false;
  const x = v as Record<string, unknown>;
  if (!Array.isArray(x.evidence)) return false;
  if (!Array.isArray(x.lead_pair) || x.lead_pair.length !== 2) return false;
  if (
    typeof x.lead_pair[0] !== 'string' ||
    typeof x.lead_pair[1] !== 'string' ||
    x.lead_pair[0].length === 0 ||
    x.lead_pair[1].length === 0
  )
    return false;
  if (!Number.isInteger(x.precedents_count)) return false;
  const n = x.precedents_count as number;
  if (n < 0 || n > 1_000_000) return false;
  // Headline must itself carry a registered source — this is the most prominent
  // text on the card and the easiest place for the LLM to smuggle a fake number.
  if (typeof x.headline_stat !== 'object' || x.headline_stat === null) return false;
  const h = x.headline_stat as Record<string, unknown>;
  if (typeof h.text !== 'string' || h.text.length === 0) return false;
  if (typeof h.source !== 'string') return false;
  if (!(CITATION_SOURCES as Record<string, unknown>)[h.source]) return false;
  return true;
}

export function isValidEvidenceRow(row: VerdictEvidenceRow): boolean {
  if (typeof row !== 'object' || row === null) return false;
  if (typeof row.stat !== 'string' || row.stat.length === 0) return false;
  // Every row must cite a registered source. Tool-verified claims should use
  // source='damage-calc'. The old `verified: true` bypass was removed because
  // a self-attesting boolean from the LLM proves nothing.
  if (typeof row.source !== 'string') return false;
  return Boolean((CITATION_SOURCES as Record<string, unknown>)[row.source as SourceId]);
}
